// src/services/offline-cache.js
// IndexedDB-backed cache + mutation queue. Two stores in one database:
//   - `cache` — keyed by string, holds GET response payloads w/ `cachedAt`
//   - `queue` — auto-keyed, holds queued mutating requests for replay
//
// See docs/system/offline-mode.md for sync semantics.

import { openDB } from 'idb';

const DB_NAME = 'eventory-offline';
const DB_VERSION = 1;
const CACHE_STORE = 'cache';
const QUEUE_STORE = 'queue';

// Stale-while-revalidate threshold for cache reads.
export const CACHE_STALE_AFTER_MS = 30 * 1000;
// Hard ceiling on cache freshness — beyond this the cache is treated as expired
// and not returned to the UI even as a stale value. 24h matches an event's
// natural lifespan (see ADR §TTL choices).
export const CACHE_HARD_TTL_MS = 24 * 60 * 60 * 1000;
// Mutation queue entry TTL. 24h so an all-day event — or an overnight WiFi
// outage — still replays its queued orders; matches the read-cache hard TTL.
export const QUEUE_ENTRY_TTL_MS = 24 * 60 * 60 * 1000;

let dbPromise = null;

function getDB() {
	if (typeof indexedDB === 'undefined') {
		return Promise.reject(new Error('IndexedDB is not available'));
	}
	if (!dbPromise) {
		dbPromise = openDB(DB_NAME, DB_VERSION, {
			upgrade(db) {
				if (!db.objectStoreNames.contains(CACHE_STORE)) {
					db.createObjectStore(CACHE_STORE);
				}
				if (!db.objectStoreNames.contains(QUEUE_STORE)) {
					db.createObjectStore(QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
				}
			},
		}).catch((err) => {
			// Reset so a later call can retry (e.g. after the user grants storage).
			dbPromise = null;
			throw err;
		});
	}
	return dbPromise;
}

// ── Cache (read) ─────────────────────────────────────────────────────────────

export async function cacheSet(key, value) {
	try {
		const db = await getDB();
		await db.put(CACHE_STORE, { value, cachedAt: Date.now() }, key);
	} catch (err) {
		// Cache writes are best-effort. A quota error or private-mode block
		// shouldn't break the request path.
		if (typeof console !== 'undefined') console.warn('offline-cache: set failed', key, err);
	}
}

export async function cacheGet(key) {
	try {
		const db = await getDB();
		const entry = await db.get(CACHE_STORE, key);
		if (!entry) return null;
		const age = Date.now() - entry.cachedAt;
		if (age > CACHE_HARD_TTL_MS) {
			// Expired — drop it and treat as a miss.
			await db.delete(CACHE_STORE, key);
			return null;
		}
		return { value: entry.value, cachedAt: entry.cachedAt, isStale: age > CACHE_STALE_AFTER_MS };
	} catch (err) {
		if (typeof console !== 'undefined') console.warn('offline-cache: get failed', key, err);
		return null;
	}
}

export async function cacheDelete(key) {
	try {
		const db = await getDB();
		await db.delete(CACHE_STORE, key);
	} catch (err) {
		if (typeof console !== 'undefined') console.warn('offline-cache: delete failed', key, err);
	}
}

export async function cacheKeys() {
	try {
		const db = await getDB();
		return await db.getAllKeys(CACHE_STORE);
	} catch (err) {
		return [];
	}
}

export async function cacheClear() {
	try {
		const db = await getDB();
		await db.clear(CACHE_STORE);
	} catch (err) {
		// Ignore.
	}
}

// Normalize a GET URL into a stable cache key. Sorts query params so e.g.
// `?a=1&b=2` and `?b=2&a=1` collapse to the same cache entry.
export function cacheKeyForUrl(url) {
	if (!url) return null;
	let pathname = url;
	let search = '';
	const queryIdx = url.indexOf('?');
	if (queryIdx >= 0) {
		pathname = url.slice(0, queryIdx);
		search = url.slice(queryIdx + 1);
	}
	const params = new URLSearchParams(search);
	const sorted = [...params.entries()].sort(([a], [b]) => a.localeCompare(b));
	const canonical = sorted.length > 0 ? `?${sorted.map(([k, v]) => `${k}=${v}`).join('&')}` : '';
	return `eventory:get:${pathname}${canonical}`;
}

// Cacheable URL patterns. Mutating actions, auth, marketing, etc. are not
// cached — they're either replayable via the queue or have no offline value.
const CACHEABLE_PATTERNS = [
	/^\/customer\/menu\//, // /customer/menu/:tableId — the kill-shot read
	/^\/customer\/validate-access-code\//, // small but useful
	/^\/customer\/orders\/[^/]+$/, // a guest's own order detail
	/^\/events\/[a-f0-9]{24}$/i, // event detail (admin/staff view)
	/^\/events\/[a-f0-9]{24}\/tables$/i, // /events/:id/tables
	/^\/events\/[a-f0-9]{24}\/kitchens$/i,
];

export function isCacheable(url) {
	if (!url || typeof url !== 'string') return false;
	return CACHEABLE_PATTERNS.some((p) => p.test(url));
}

// ── Queue (mutations) ────────────────────────────────────────────────────────

export async function queueAdd(entry) {
	const record = {
		url: entry.url,
		method: entry.method,
		data: entry.data ?? null,
		headers: entry.headers ?? {},
		idempotencyKey: entry.idempotencyKey,
		attempts: entry.attempts ?? 0,
		queuedAt: entry.queuedAt ?? Date.now(),
		lastError: entry.lastError ?? null,
	};
	const db = await getDB();
	const id = await db.add(QUEUE_STORE, record);
	return { ...record, id };
}

export async function queueUpdate(id, patch) {
	const db = await getDB();
	const tx = db.transaction(QUEUE_STORE, 'readwrite');
	const existing = await tx.store.get(id);
	if (!existing) {
		await tx.done;
		return null;
	}
	const next = { ...existing, ...patch };
	await tx.store.put(next);
	await tx.done;
	return next;
}

export async function queueRemove(id) {
	const db = await getDB();
	await db.delete(QUEUE_STORE, id);
}

export async function queueAll() {
	try {
		const db = await getDB();
		return await db.getAll(QUEUE_STORE);
	} catch (err) {
		return [];
	}
}

export async function queueCount() {
	try {
		const db = await getDB();
		return await db.count(QUEUE_STORE);
	} catch (err) {
		return 0;
	}
}

// Drop entries past TTL. Returns the count dropped so callers can log it.
export async function queuePurgeExpired(now = Date.now()) {
	const db = await getDB();
	const tx = db.transaction(QUEUE_STORE, 'readwrite');
	let dropped = 0;
	let cursor = await tx.store.openCursor();
	while (cursor) {
		if (now - (cursor.value.queuedAt || 0) > QUEUE_ENTRY_TTL_MS) {
			await cursor.delete();
			dropped += 1;
		}
		cursor = await cursor.continue();
	}
	await tx.done;
	return dropped;
}

export async function queueClear() {
	try {
		const db = await getDB();
		await db.clear(QUEUE_STORE);
	} catch (err) {
		// Ignore.
	}
}

// Generate an idempotency key. Falls back to a Math.random-based UUID v4 shape
// in the rare environment without `crypto.randomUUID` (e.g. older Safari).
export function makeIdempotencyKey() {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	// RFC 4122 v4 fallback.
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

// Exposed for tests so they can reset state between cases without leaking
// between specs. Not part of the runtime API. Clears both stores rather
// than deleting the database — deleteDatabase blocks indefinitely in
// fake-indexeddb if any other connection is still open in the same tick.
export async function __resetForTests() {
	if (typeof indexedDB === 'undefined') return;
	try {
		const db = await getDB();
		const tx = db.transaction([CACHE_STORE, QUEUE_STORE], 'readwrite');
		await Promise.all([tx.objectStore(CACHE_STORE).clear(), tx.objectStore(QUEUE_STORE).clear()]);
		await tx.done;
	} catch (err) {
		// Ignore.
	}
}

// Close the cached DB handle without deleting the database. Used by tests to
// simulate a page reload: the next access reopens the DB and reads back the
// persisted entries.
export async function __closeDBForTests() {
	if (!dbPromise) return;
	try {
		const db = await dbPromise;
		db.close();
	} catch (err) {
		// Ignore.
	}
	dbPromise = null;
}
